/**
 * Which facility group a facility is in, for the pages whose rows carry the facility but
 * not its group: Claim detail and Worklist assignments. The filter bar's Facility group
 * filter (data/filters.tsx) selects their rows by it, exactly.
 *
 * The group is read off the bundle's crosstab, which is what the Overview slices on. A
 * facility filed under more than one group takes the one most of its claims sit in, and
 * every blank spelling is the one "Unknown" the Overview draws (review #177).
 */
import type { CrosstabDict } from './types'
import type { ClaimDetailData } from './claimDetail'
import { isNoClaim } from './noClaim'
import { sameValue, valueName } from './blankValue'

/** The tally's winner: the key with the most claims. */
const most = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1])[0]![0]

/** Facility name → its group, off the crosstab. */
export function facilityGroupOf(ct: CrosstabDict): Map<string, string> {
  const tally = new Map<string, Map<string, number>>()
  for (const row of ct.rows) {
    const f = ct.facilities[row[5]]
    const g = ct.facilityGroups[row[4]]
    // The no-claim bucket is payroll, not a facility: no claim row carries it.
    if (!f || !g || isNoClaim(f)) continue
    let m = tally.get(f)
    if (!m) tally.set(f, (m = new Map()))
    m.set(g, (m.get(g) ?? 0) + row[6])
  }
  const out = new Map<string, string>()
  for (const [f, m] of tally) out.set(f, valueName(most(m)))
  return out
}

/**
 * Medevolve facility id → its group, through Claim detail's rows: each carries the id
 * (`fid`) and the facility as the claims name it, which is the name the crosstab uses.
 * Worklist assignments name a facility as Medevolve's configuration does, which differs
 * from the claims' two times in three, so they are placed by id, not by name.
 *
 * Only ids some claim the caller can see carries are placed; most claims wins. Null on
 * a claims file built before the ids (2026-10-12).
 */
export function facilityGroupById(
  claims: ClaimDetailData,
  groupOf: ReadonlyMap<string, string>,
): Map<number, string> | null {
  const fid = claims.cols.fid
  if (!fid) return null
  const tally = new Map<number, Map<string, number>>()
  for (let i = 0; i < claims.n; i++) {
    const id = fid[i]
    if (id == null) continue
    const g = groupOf.get(claims.dict.f[claims.cols.f[i]!]!)
    if (g === undefined) continue
    let m = tally.get(id)
    if (!m) tally.set(id, (m = new Map()))
    m.set(g, (m.get(g) ?? 0) + 1)
  }
  const out = new Map<number, string>()
  for (const [id, m] of tally) out.set(id, most(m))
  return out
}

/** Whether a group is one of the filter's picks, blank spellings as one. */
export const inGroups = (group: string | undefined, picks: readonly string[]) =>
  picks.some((p) => sameValue(group, p))
